import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

function startOfToday(): Date {
  return dayjs().startOf('day').toDate();
}

/**
 * Local midnight of the current day, as state that advances when the day
 * does. Anything memoised on "today" (the inbox hiding tasks due after today,
 * BAR-2679) must re-run at midnight and when a backgrounded tab is brought
 * back, or a list opened yesterday keeps yesterday's idea of "due today".
 */
export function useLocalDayStart(): Date {
  const [dayStart, setDayStart] = useState<Date>(startOfToday);

  useEffect(() => {
    const tick = (): void => {
      setDayStart((prev) => {
        const next = startOfToday();
        return next.getTime() === prev.getTime() ? prev : next;
      });
    };

    // +1s past midnight so a timer that fires a hair early still lands on the new day.
    const msUntilMidnight = dayjs().add(1, 'day').startOf('day').diff(dayjs()) + 1000;
    const timer = window.setTimeout(tick, msUntilMidnight);

    // Timers are throttled or suspended in background tabs; re-check on return.
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') {
        tick();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', tick);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', tick);
    };
  }, [dayStart]);

  return dayStart;
}
